import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { KpiSummary, WaveformJson } from "../api/runs";
import { fetchKpiSummary, fetchWaveformJson } from "../api/runs";
import type { DamperSettingPayload, Run, Test } from "../api/tests";
import { KpiComparisonTable, type KpiRowState } from "./KpiComparisonTable";
import {
  MultiRunOverlayChart, type SelectedRunWaveform,
} from "./MultiRunOverlayChart";
import type { R0RecordedReference } from "./multiRunOverlayAdapter";
import { TrackPositionStrip } from "./TrackPositionStrip";

export type ComparisonPaneProps = {
  test: Test;
  selectedRunIds: number[];
  onClear: () => void;
};

type ArtifactKind = "kpi" | "waveform";

type FetchState<T> =
  | { kind: "loading" }
  | { kind: "ready"; data: T }
  | { kind: "error"; message: string };

type RunFetchState = {
  kpi: FetchState<KpiSummary>;
  waveform: FetchState<WaveformJson>;
};

const KEY = (runId: number, kind: ArtifactKind) => `${runId}:${kind}`;

// Mirrors TestDetailView.describeRunIntent — kept local rather than extracted
// to a shared module (YAGNI for v1).
function deriveRunIntent(run: Run): string {
  const setting = run.damper_setting as DamperSettingPayload | null | undefined;
  const corners = setting?.corners ?? {};
  const touched = Object.entries(corners)
    .filter(([, edit]) => edit && Object.keys(edit).length > 0)
    .map(([corner, edit]) => {
      const fields = [
        edit.damper_code ? "code" : null,
        edit.clicks ? "clicks" : null,
      ].filter(Boolean);
      return fields.length > 0
        ? `${corner.toUpperCase()} ${fields.join("+")}`
        : corner.toUpperCase();
    });
  if (touched.length === 0) return "baseline";
  return `edited ${touched.join(" · ")}`;
}

function r0FromTest(test: Test): Run | null {
  const r0 = (test.runs ?? []).find((r) => r.run_number === 0);
  return r0 ?? null;
}

function errorMessage(e: unknown): string {
  if (e instanceof Error) return e.message;
  return "Unknown error";
}

export function ComparisonPane({
  test, selectedRunIds, onClear,
}: ComparisonPaneProps) {
  const r0 = r0FromTest(test);
  const r0Id = r0?.id ?? null;
  const r0InSelection = r0Id != null && selectedRunIds.includes(r0Id);

  const idsToFetch = useMemo(() => {
    const set = new Set<number>(selectedRunIds);
    if (r0Id != null && !r0InSelection) set.add(r0Id);
    return Array.from(set);
  }, [selectedRunIds, r0Id, r0InSelection]);

  const [fetches, setFetches] = useState<Map<number, RunFetchState>>(new Map());
  const controllersRef = useRef<Map<string, AbortController>>(new Map());

  const spawnFetch = useCallback((id: number, kind: ArtifactKind) => {
    const key = KEY(id, kind);
    controllersRef.current.get(key)?.abort();
    const ctrl = new AbortController();
    controllersRef.current.set(key, ctrl);
    const promise =
      kind === "kpi"
        ? fetchKpiSummary(id, { signal: ctrl.signal })
        : fetchWaveformJson(id, { signal: ctrl.signal });
    promise.then(
      (data) => {
        if (ctrl.signal.aborted) return;
        setFetches((prev) => {
          const next = new Map(prev);
          const cur = next.get(id) ?? {
            kpi: { kind: "loading" }, waveform: { kind: "loading" },
          };
          next.set(id, { ...cur, [kind]: { kind: "ready", data } } as RunFetchState);
          return next;
        });
      },
      (err: unknown) => {
        if (ctrl.signal.aborted) return;
        if ((err as { name?: string }).name === "AbortError") return;
        setFetches((prev) => {
          const next = new Map(prev);
          const cur = next.get(id) ?? {
            kpi: { kind: "loading" }, waveform: { kind: "loading" },
          };
          next.set(id, {
            ...cur,
            [kind]: { kind: "error", message: errorMessage(err) },
          } as RunFetchState);
          return next;
        });
      },
    );
  }, []);

  useEffect(() => {
    setFetches((prev) => {
      const next = new Map<number, RunFetchState>();
      for (const id of idsToFetch) {
        if (prev.has(id)) {
          next.set(id, prev.get(id)!);
        } else {
          next.set(id, {
            kpi: { kind: "loading" },
            waveform: { kind: "loading" },
          });
          spawnFetch(id, "kpi");
          spawnFetch(id, "waveform");
        }
      }
      for (const [key, ctrl] of controllersRef.current.entries()) {
        const idStr = key.split(":")[0];
        if (!idsToFetch.includes(Number(idStr))) {
          ctrl.abort();
          controllersRef.current.delete(key);
        }
      }
      return next;
    });
  }, [idsToFetch, spawnFetch]);

  useEffect(() => {
    return () => {
      for (const ctrl of controllersRef.current.values()) ctrl.abort();
      controllersRef.current.clear();
    };
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      const ev = e as CustomEvent<{ runId: number; kind: ArtifactKind }>;
      const { runId, kind } = ev.detail;
      setFetches((prev) => {
        const next = new Map(prev);
        const cur = next.get(runId);
        if (cur) {
          next.set(runId, { ...cur, [kind]: { kind: "loading" } } as RunFetchState);
        }
        return next;
      });
      spawnFetch(runId, kind);
    };
    window.addEventListener("comparison-pane-retry", handler as EventListener);
    return () => window.removeEventListener(
      "comparison-pane-retry", handler as EventListener,
    );
  }, [spawnFetch]);

  const selectedRuns = (test.runs ?? []).filter((r) =>
    selectedRunIds.includes(r.id),
  );
  const selectedRowsState = selectedRuns.map((run) => {
    const state = fetches.get(run.id);
    const kpi: KpiRowState = state?.kpi ?? { kind: "loading" };
    const waveform = state?.waveform ?? { kind: "loading" };
    return { run, kpi, waveform };
  });

  const allSettled = selectedRowsState.every(
    (r) => r.kpi.kind !== "loading" && r.waveform.kind !== "loading",
  );
  const waveformReady = selectedRowsState.filter(
    (r) => r.waveform.kind === "ready",
  );

  const r0State = r0Id != null ? fetches.get(r0Id) : undefined;
  const deltasAvailable = r0State?.kpi.kind === "ready";
  const r0WfReady =
    r0State?.waveform.kind === "ready" ? r0State.waveform.data : null;
  const r0RecordedReference: R0RecordedReference | null =
    r0WfReady && r0WfReady.recorded != null
      ? {
          time_s: r0WfReady.time_s,
          recorded: {
            lf: r0WfReady.recorded.lf ?? [],
            rf: r0WfReady.recorded.rf ?? [],
            lr: r0WfReady.recorded.lr ?? [],
            rr: r0WfReady.recorded.rr ?? [],
          },
        }
      : null;

  if (selectedRowsState.some(
    (r) => r.kpi.kind === "loading" || r.waveform.kind === "loading",
  )) {
    return (
      <div className="comparison-pane">
        <div className="comparison-pane-chrome">
          <button type="button" className="btn" onClick={onClear}>Clear</button>
        </div>
        <div className="comparison-pane-skeleton">Loading…</div>
      </div>
    );
  }

  if (allSettled && waveformReady.length < 2) {
    const failures = selectedRowsState
      .filter((r) =>
        r.kpi.kind === "error" || r.waveform.kind === "error",
      )
      .map((r) => ({
        runNumber: r.run.run_number,
        kpiErr: r.kpi.kind === "error" ? r.kpi.message : null,
        wfErr: r.waveform.kind === "error" ? r.waveform.message : null,
      }));
    return (
      <div className="comparison-pane comparison-pane-error">
        <div className="comparison-pane-chrome">
          <button type="button" className="btn" onClick={onClear}>Clear</button>
        </div>
        <p>Couldn't load enough runs to compare.</p>
        <ul>
          {failures.map((f) => (
            <li key={f.runNumber}>
              R{f.runNumber}:{" "}
              {f.kpiErr ? `kpi=${f.kpiErr} ` : ""}
              {f.wfErr ? `waveform=${f.wfErr}` : ""}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  const chartRuns: SelectedRunWaveform[] = waveformReady.map((r) => {
    const wfReady = r.waveform as { kind: "ready"; data: WaveformJson };
    return {
      id: r.run.id,
      runNumber: r.run.run_number,
      intentLabel: deriveRunIntent(r.run) || `R${r.run.run_number}`,
      predicted: {
        lf: wfReady.data.predicted.lf,
        rf: wfReady.data.predicted.rf,
        lr: wfReady.data.predicted.lr,
        rr: wfReady.data.predicted.rr,
      },
    };
  });
  const timeS = waveformReady[0]
    ? (waveformReady[0].waveform as { kind: "ready"; data: WaveformJson }).data
        .time_s
    : [];

  return (
    <div className="comparison-pane">
      <div className="comparison-pane-chrome">
        <button type="button" className="btn" onClick={onClear}>Clear</button>
      </div>
      {!deltasAvailable ? (
        <p className="banner">
          No R0 baseline available — showing absolute values.
        </p>
      ) : null}
      <KpiComparisonTable
        rows={selectedRowsState.map((r) => ({
          run: r.run,
          kpi: r.kpi,
          intent: deriveRunIntent(r.run),
        }))}
        r0RunId={r0Id}
        deltasAvailable={deltasAvailable}
        onRetry={(runId, kind) =>
          window.dispatchEvent(
            new CustomEvent("comparison-pane-retry", {
              detail: { runId, kind },
            }),
          )
        }
      />
      <MultiRunOverlayChart
        timeS={timeS}
        selectedRuns={chartRuns}
        r0RecordedReference={r0RecordedReference}
      />
      <TrackPositionStrip testId={test.id} />
    </div>
  );
}
